import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import '@sweetalert2/theme-dark/dark.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faWater, faThermometerHalf, faBolt, faCogs, faVial, faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import {useTranslation} from "react-i18next";

function AdminFarmPond() {
    const [farms, setFarms] = useState([]);
    const [selectedFarm, setSelectedFarm] = useState('');
    const [pondStatus, setPondStatus] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [chartData, setChartData] = useState(null);
    const { t } = useTranslation();
    const [districts, setDistricts] = useState([]);
    const [ngos, setNgos] = useState([]);
    const [filterNgo, setFilterNgo] = useState('');
    const [filterDistrict, setFilterDistrict] = useState('');
    const [filterSubdistrict, setFilterSubdistrict] = useState('');
    const [filterSubdistricts, setFilterSubdistricts] = useState([]);


    useEffect(() => {
        fetchFarms();
    }, []);

    useEffect(() => {
        setSelectedFarm('');
        setPondStatus([]);
    }, [filterDistrict, filterSubdistrict]);


    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/districts`);
                setDistricts(response.data);
            } catch (err) {
                console.error('Failed to fetch districts', err);
            }
        };

        const fetchNgos = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/ngos`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setNgos(res.data);
            } catch (err) {
                console.error('Failed to fetch NGOs', err);
            }
        };

        fetchDistricts();
        fetchNgos();
    }, []);


    const fetchFarms = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/farm/all/list`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFarms(response.data.farms);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch farms.', 'error');
        }
    };

    const fetchPondStatus = async (farmId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/pond-status/${farmId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setPondStatus(response.data);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch pond status.', 'error');
        }
    };

    const fetchSensorSummary = async (pondId) => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/sensor-24-summery/${pondId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setChartData(response.data.data);
            setIsModalOpen(true);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch sensor summary.', 'error');
        }
    };


    const handleFarmSelect = (e) => {
        const farmId = e.target.value;
        setSelectedFarm(farmId);
        if (farmId) {
            fetchPondStatus(farmId);
        } else {
            setPondStatus([]);
        }
    };

    const handlePondClick = (pondId) => {
        fetchSensorSummary(pondId);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setChartData(null);
    };


    const getSensorIcon = (sensorName) => {
        switch (sensorName) {
            case 'DO':
                return faWater;
            case 'Water Temp':
                return faThermometerHalf;
            case 'Ammonia':
            case 'Water PH':
                return faVial;
            default:
                return faVial;
        }
    };

    const generateIndividualSensorCharts = () => {
        if (!chartData) return null;

        return chartData.sensors.map((sensor) => {
            const data = sensor.data.map((entry) => ({
                time: entry.datetime,
                value: parseFloat(entry.averageValue),
            }));

            const chartWidth = window.innerWidth < 640 ? 280 : window.innerWidth < 768 ? 350 : 500;
            const chartHeight = window.innerWidth < 640 ? 180 : window.innerWidth < 768 ? 220 : 300;

            return (
                <div key={sensor.sensorType} className="mb-6 md:mb-8">
                    <h3 className="text-base md:text-lg font-semibold mb-3 md:mb-4">
                        {sensor.sensorType} ({sensor.data[0]?.unit || ''})
                    </h3>
                    <LineChart
                        width={chartWidth}
                        height={chartHeight}
                        data={data}
                        margin={{top: 20, right: 30, left: 40, bottom: 30}}
                    >
                        <CartesianGrid strokeDasharray="3 3"/>
                        <XAxis
                            dataKey="time"
                            label={{value: 'Time', position: 'bottom', offset: 10}}
                        />
                        <YAxis
                            label={{value: 'Value', angle: -90, position: 'insideLeft', offset: 10}}
                        />
                        <Tooltip/>
                        <Legend wrapperStyle={{bottom: -10}}/>
                        <Line type="monotone" dataKey="value" stroke="rgba(75, 192, 192, 0.6)" strokeWidth={4}/>
                    </LineChart>
                </div>
            );
        });
    };

    return (
        <div className="container mx-auto p-4 md:p-6">

            <h1 className="text-2xl md:text-3xl font-bold  mb-4 md:mb-6">{t('pond_status')}</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
                <div>
                    <label className="block text-gray-700 mb-2 text-sm md:text-base">
                        {t('Filter by District')}
                    </label>
                    <select
                        value={filterDistrict}
                        onChange={(e) => {
                            const selected = e.target.value;
                            setFilterDistrict(selected);
                            const found = districts.find(d => d.name === selected);
                            setFilterSubdistricts(found ? found.subdistricts : []);
                            setFilterSubdistrict('');
                        }}
                        className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm md:text-base"
                    >
                        <option value="">{t('All')} {t('Districts')}</option>
                        {districts.map((d) => (
                            <option key={d._id} value={d.name}>{t(d.name)}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-gray-700 mb-2 text-sm md:text-base">
                        {t('Filter by Subdistrict')}
                    </label>
                    <select
                        value={filterSubdistrict}
                        onChange={(e) => setFilterSubdistrict(e.target.value)}
                        className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm md:text-base"
                        disabled={!filterDistrict}
                    >
                        <option value="">{t('All')} {t('Subdistricts')}</option>
                        {filterSubdistricts.map((s, i) => (
                            <option key={i} value={s}>{t(s)}</option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mb-6 md:mb-8">
                <label className="block text-gray-700 mb-2 text-sm md:text-base">
                    {t('Filter by NGO')}
                </label>
                <select
                    value={filterNgo}
                    onChange={(e) => setFilterNgo(e.target.value)}
                    className="w-full px-3 md:px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm md:text-base"
                >
                    <option value="">{t('All')} {t('NGOs')}</option>
                    {ngos.map((n) => (
                        <option key={n._id} value={n._id}>{n.name}</option>
                    ))}
                </select>
            </div>


            <div className="mb-6 md:mb-8">
                <label htmlFor="farmSelect" className="block text-gray-700 mb-2 text-sm md:text-base">
                    {t('select_farm')} :
                </label>
                <select
                    id="farmSelect"
                    value={selectedFarm}
                    onChange={handleFarmSelect}
                    className="w-full p-2 md:p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm md:text-base"
                >
                    <option value="">-- {t('select_farm')} --</option>
                    {farms
                        .filter((farm) => {
                            const matchDistrict = filterDistrict ? farm.district === filterDistrict : true;
                            const matchSubdistrict = filterSubdistrict ? farm.subdistrict === filterSubdistrict : true;
                            const matchNgo = filterNgo ? farm.ngo_id === filterNgo : true;
                            return matchDistrict && matchSubdistrict && matchNgo;
                        })
                        .map((farm) => (
                        <option key={farm._id} value={farm._id}>
                            {farm.name} ( {farm.location} )
                        </option>
                    ))}
                </select>
            </div>

            {/* Display Pond Status */}
            {pondStatus.length > 0 && (
                <div className="grid grid-cols-1 gap-4 md:gap-6">
                    {pondStatus.map((pond) => (
                        <div key={pond.pond_id}
                            className="bg-white shadow-lg rounded-lg overflow-hidden transition-transform transform hover:scale-105 duration-300 flex flex-col h-full"

                        >
                            {/* Card Header */}
                            <div className="bg-cyan-600 text-white p-3 md:p-4 flex flex-col items-center">
                                <h2 className="text-lg md:text-2xl font-bold mb-2">{pond.pond_name}</h2>
                                {/* Button with Icon */}
                                <button
                                    onClick={() => handlePondClick(pond.pond_id)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-3 md:px-4 rounded flex items-center mt-2 mb-2 text-sm md:text-base"
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} className="mr-2 text-sm md:text-base"/> {/* Adjust icon */}
                                    {t('water_quality_summery')}
                                </button>
                            </div>


                            {/* Card Body */}
                            <div className="p-4 md:p-6 flex-1">
                                <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
                                    {/* Sensor Data Section */}
                                    <div className="flex-1 bg-gray-50 p-3 md:p-4 rounded-lg shadow-md">
                                        <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">{t('parameter')} ( {pond.last_updated} )</h3>
                                        <div className="space-y-3 md:space-y-4">
                                            {pond.sensors.map((sensor, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center justify-between p-2 md:p-3 bg-white rounded-lg shadow-sm"
                                                >
                                                    <div className="flex items-center">
                                                        <FontAwesomeIcon
                                                            icon={getSensorIcon(sensor.sensor_name)}
                                                            className="text-blue-500 mr-2 md:mr-3 text-sm md:text-base"
                                                        />
                                                        <h4 className="text-sm md:text-md font-semibold text-gray-800">{sensor.sensor_name}</h4>
                                                    </div>
                                                    <p className="text-gray-700 text-sm md:text-base">
                                                        <span className="font-bold">{sensor.value}</span> {sensor.unit}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Conductor Data Section */}
                                    <div className="flex-1 bg-gray-50 p-3 md:p-4 rounded-lg shadow-md">
                                        <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">{t('peripheral_control_panel')}</h3>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                                            {pond.conductors.length === 0 ? (
                                                <p className="text-gray-500 text-sm md:text-base">No modes available</p>
                                            ) : (
                                                pond.conductors.map((conductor, index) => (
                                                    <div
                                                        key={index}
                                                        className="flex flex-col p-2 md:p-3 bg-white rounded-lg shadow-sm"
                                                    >
                                                        <div className="flex items-center justify-between mb-2">
                                                            <div className="flex items-center">
                                                                <FontAwesomeIcon icon={faBolt}
                                                                                 className="text-yellow-500 mr-2 md:mr-3 text-sm md:text-base"/>
                                                                <h4 className="text-sm md:text-md font-semibold text-gray-800">
                                                                    {t('mode')} : {index + 1}
                                                                </h4>
                                                            </div>
                                                            <p className={`font-bold text-xs md:text-sm ${
                                                                    conductor.status === 'on' ? 'text-green-500' : 'text-red-500'
                                                                }`}
                                                            >
                                                                {conductor.status === 'on'?t('manual'):t('auto')}
                                                            </p>
                                                        </div>
                                                        <div className="mt-3 md:mt-4">
                                                            <h5 className="text-sm md:text-md font-semibold text-gray-700 mb-2">{t('peripherals')}</h5>
                                                            {conductor.peripherals.length === 0 ? (
                                                                <p className="text-gray-500 text-xs md:text-sm">No peripherals
                                                                    available</p>
                                                            ) : (
                                                                conductor.peripherals.map((peripheral, pIndex) => (
                                                                    <div
                                                                        key={pIndex}
                                                                        className="flex items-center justify-between p-2 md:p-3 bg-gray-100 rounded-lg shadow-sm mt-2"
                                                                    >
                                                                        <div className="flex items-center flex-1 min-w-0">
                                                                            <FontAwesomeIcon icon={faCogs}
                                                                                             className="text-green-500 mr-2 md:mr-3 text-sm md:text-base flex-shrink-0"/>
                                                                            <h4 className="text-xs md:text-sm font-medium text-gray-800 truncate">{peripheral.name}</h4>
                                                                        </div>
                                                                        <p
                                                                            className={`font-bold text-xs md:text-sm flex-shrink-0 ml-2 ${
                                                                                peripheral.status === 'on' ? 'text-green-500' : 'text-red-500'
                                                                            }`}
                                                                        >
                                                                            {peripheral.status === 'on'?t('on'):t('off')}
                                                                        </p>
                                                                    </div>
                                                                ))
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isModalOpen && (
                <div className="fixed z-50 inset-0 bg-black bg-opacity-50 overflow-auto text-center">
                    <div className="flex items-center justify-center min-h-screen px-2 md:px-4 py-4 md:py-8 text-center">
                        <div
                            className="bg-white rounded-lg shadow-2xl w-full max-w-xs sm:max-w-lg md:max-w-4xl lg:max-w-6xl mx-auto p-3 md:p-6 transform transition-all text-center">
                            {/* Modal Header */}
                            <div className="flex justify-between items-center border-b pb-3 md:pb-4 mb-3 md:mb-4">
                                <h2 className="text-lg md:text-2xl font-bold text-gray-800 text-center">
                                    {chartData?.pond??"No"} - {t('parameter_summery')}
                                </h2>
                                <button onClick={closeModal} className="text-gray-500 hover:text-gray-800">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" fill="none"
                                         viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </button>
                            </div>

                            {/* Modal Body with Two Columns */}
                            <div className="overflow-y-auto max-h-80 md:max-h-96">
                                {chartData ? (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6 w-full">
                                        {generateIndividualSensorCharts().map((chart, index) => (
                                            <div key={index} className="bg-white p-2 md:p-4 rounded-lg shadow-md">
                                                {chart}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-gray-500 w-full text-sm md:text-base">Loading...</p>
                                )}
                            </div>


                            {/* Modal Footer */}
                            <div className="mt-4 md:mt-6 text-right">
                                <button
                                    onClick={closeModal}
                                    className="bg-red-500 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-red-600 focus:outline-none transition duration-200 text-sm md:text-base"
                                >
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

            )}
        </div>
    );
}

export default AdminFarmPond;
